import classNames from "classnames";
import React, { CSSProperties, ReactElement } from "react";
import SvgDragIndicator from "../icons/DragIndicator";
import SvgSubdirectoryArrowLeft from "../icons/SubdirectoryArrowLeft";
import SvgSubdirectoryArrowRight from "../icons/SubdirectoryArrowRight";
import * as bd from "../index";

interface ColumnProps {
    accessor?: string;
    Header?: string | React.ReactNode;

    name?: string;
    title?: string;
    width?: any;
    minWidth?: any;

    cellCSS?: string | (({ row, rowIndex, columnName, columnIndex }) => string);
    headerCSS?: string | (({ columnName, columnIndex }) => string);

    getDisplayValue?: ({ value, row, rowIndex, columnName, columnIndex }) => ReactElement;
    Cell?: ({ value, row, rowIndex, columnName, columnIndex }) => ReactElement;
    render?: ({ row, rowIndex, columnName, columnIndex, defaultProps }) => ReactElement;
    onCellClick?: ({ row, rowIndex, columnName, columnIndex, event }) => void;
    onCellDoubleClick?: ({ row, rowIndex, columnName, columnIndex, event }) => void;
}

interface BasicTableProps {
    fullWidth?: boolean;
    hover?: boolean;
    striped?: boolean;
    compact?: boolean;
    nowrap?: boolean;
    fixed?: boolean;
    columns?: ColumnProps[];
    data?: any[];
    selectedIndices?: any[];
    selectedRows?: any[];
    multiSelect?: boolean;
    singleSelect?: boolean;
    hideCheckbox?: boolean;
    draggable?: boolean;
    disableToggleOnClick?: boolean;

    className?: string;
    style?: CSSProperties | undefined;
    maxHeight?: any;
    maxWidth?: any;
    hideHeader?: boolean;
    actions?: React.ReactNode;

    children?: React.ReactNode;

    cellCSS?: string | (({ row, rowIndex, columnName, columnIndex }) => string);
    headerCSS?: ({ columnName, columnIndex }) => string;
    renderCell?: ({ row, columnName, value, rowIndex, columnIndex }) => any;
    renderTD?: ({ row, columnName, rowIndex, columnIndex, defaultProps, renderCell }) => React.ReactNode;
    onSelectionChanged?: (indices: any, currrentIndex: number) => void;
    onDragStart?: (ev: any, fromRowIndex: number) => void;
    onDragEnd?: (ev: any, fromRowIndex: number) => void;
    onCanDrop?: (ev: any, fromRowIndex: number, newRowIndex: number, newColumnIndex: number) => boolean;
    onDrop?: (ev: any, fromRowIndex: number, newRowIndex: number, newColumnIndex: number) => void;
    onRowClick?: ({ row, rowIndex, event }) => void;
    onRowDoubleClick?: ({ row, rowIndex, event }) => void;
    [x: string]: any;
}

export const BasicTable = ({
    hover,
    striped,
    compact,
    nowrap,
    fixed,
    columns,
    data,
    selectedIndices,
    multiSelect,
    singleSelect,
    hideCheckbox,
    draggable,
    disableToggleOnClick,
    className,
    style,
    maxHeight,
    maxWidth,
    children,
    actions,
    hideHeader,

    //renderCell is depriciated
    renderCell,

    //renderTD is depriciated
    renderTD,

    onSelectionChanged,
    onDragStart,
    onDragEnd,
    onCanDrop,
    onDrop,
    cellCSS,
    headerCSS,
    onRowClick,
    onRowDoubleClick,
    ...props
}: BasicTableProps) => {
    if (multiSelect) singleSelect = false;
    const dragStartRowIndex = React.useRef();
    const cn = classNames("bd-table", className, {
        "bd-table-full-width": !fixed,
        "bd-table-hover": hover,
        "bd-table-striped": striped,
        "bd-table-compact": compact,
        "bd-table-nowrap": nowrap,
        "bd-table-fixed": fixed,
        "cur-pointer": (singleSelect || multiSelect) && !disableToggleOnClick,
    });

    const isSelected = (idx: number) => selectedIndices && selectedIndices.some((x) => x === idx);

    const toggleSelection = (idx, isDoubleClick) => {
        if (!onSelectionChanged) return;
        let list = [];

        const is_selected = !isDoubleClick && isSelected(idx);
        if (singleSelect) {
            list = is_selected ? [] : [idx];
        } else {
            list = is_selected ? selectedIndices.filter((x) => x !== idx) : [...selectedIndices, idx];
        }

        onSelectionChanged(list, idx);
    };

    const toggleAllSelection = () => {
        if (!onSelectionChanged) return;
        let list = [];
        if (selectedIndices.length < data.length)
            for (let i = 0; i < data.length; i++) {
                list.push(i);
            }
        onSelectionChanged(list, null);
    };

    const handleRowClick = (row, idx, event) => {
        if (!disableToggleOnClick) {
            if (multiSelect || singleSelect) toggleSelection(idx, event.detail > 1);
        }
        onRowClick && onRowClick({ row, rowIndex: idx, event });
    };

    const handleCellClick = (idx) => {
        if (multiSelect || singleSelect) toggleSelection(idx, false);
    };

    const getHeaderCSS = ({ columnName, columnIndex }) => {
        return headerCSS instanceof Function ? headerCSS({ columnName, columnIndex }) : headerCSS;
    };

    const getCellCSS = (args) => {
        return cellCSS instanceof Function ? cellCSS(args) : cellCSS;
    };

    const getTHProps = (c: ColumnProps, columnIndex: number) => {
        let p = { style: { width: c.width, minWidth: c.minWidth ?? c.width } };
        const args = { columnName: c.name ?? c.accessor, columnIndex };
        const css = getHeaderCSS(args);
        const css_column = c.headerCSS instanceof Function ? c.headerCSS(args) : c.headerCSS;
        return { ...p, className: classNames(css, css_column) };
    };

    const getTDProps = (row, rowIndex, c: ColumnProps, columnIndex: number) => {
        const args = { row, rowIndex, columnName: c.name ?? c.accessor, columnIndex };
        const css = getCellCSS(args);
        const css_column = c.cellCSS instanceof Function ? c.cellCSS(args) : c.cellCSS;
        let p = { className: classNames(css, css_column) };
        if (hideHeader) p["style"] = { width: c.width, minWidth: c.width };
        return p;
    };

    const onDragStartHandler = (ev, rowIndex) => {
        ev.dataTransfer.effectAllowed = "move";
        dragStartRowIndex.current = rowIndex;
        ev.dataTransfer.setDragImage(new Image(), 0, 0);
        onDragStart && onDragStart(ev, rowIndex);
    };

    const onDragEndHandler = (ev, rowIndex) => {
        onDragEnd && onDragEnd(ev, rowIndex);
    };

    const onDragOverHandler = (ev, newRowIndex, newColumnIndex) => {
        const idx = dragStartRowIndex.current;
        if (onCanDrop && onCanDrop(ev, idx, newRowIndex, newColumnIndex)) ev.preventDefault();
    };

    const onDropHandler = (ev, newRowIndex, newColumnIndex) => {
        const idx = dragStartRowIndex.current;
        onDrop && onDrop(ev, idx, newRowIndex, newColumnIndex);
        ev.preventDefault();
    };

    const getDisplayValue = (row, columnName, rowIndex, columnIndex) => {
        const col = columns[columnIndex];
        const value = !row || !columnName ? null : row[columnName];
        if (col.getDisplayValue) return col.getDisplayValue({ value, row, columnName, rowIndex, columnIndex });
        if (col.Cell) return col.Cell({ value, row, columnName, rowIndex, columnIndex });
        // renderCell is depriciated
        return renderCell ? renderCell({ row, columnName, rowIndex, columnIndex, value }) : value;
    };

    const renderHandler = (row, rowIndex, column, columnIndex) => {
        const col = columns[columnIndex];
        const col_name = col.name ?? col.accessor;
        const onClick = (event) => {
            if (col.onCellClick && event.detail == 1) col.onCellClick({ row, rowIndex, columnName: col_name, columnIndex, event });
        };
        const onDoubleClick = (event) => {
            if (col.onCellDoubleClick) col.onCellDoubleClick({ row, rowIndex, columnName: col_name, columnIndex, event });
        };
        const defaultProps = {
            ...getTDProps(row, rowIndex, column, columnIndex),
            key: rowIndex + "." + columnIndex,
            onDragOver: (ev) => onDragOverHandler(ev, rowIndex, columnIndex),
            onDragEnter: (ev) => onDragOverHandler(ev, rowIndex, columnIndex),
            onDrop: (ev) => onDropHandler(ev, rowIndex, columnIndex),
            onClick,
            onDoubleClick,
        };

        const defaultGetDisplayValue = () => getDisplayValue(row, col_name, rowIndex, columnIndex);
        if (col.render) return col.render({ row, columnName: col_name, rowIndex, columnIndex, defaultProps });

        //-------
        // depriciated

        if (renderTD) {
            const elem = renderTD({
                row,
                columnName: col_name,
                rowIndex,
                columnIndex,
                defaultProps,
                renderCell: defaultGetDisplayValue,
            });
            return elem ?? <React.Fragment key={rowIndex + "." + columnIndex}></React.Fragment>;
        }
        return <td {...defaultProps}>{defaultGetDisplayValue()}</td>;
    };

    const canShowCheckBox = !hideCheckbox && (draggable || multiSelect || singleSelect);

    return (
        <>
            <div className={cn} style={{ ...style, maxHeight, maxWidth }} {...props}>
                {data && data.length > 0 && (
                    <table>
                        {!hideHeader && (
                            <thead>
                                <tr>
                                    {canShowCheckBox && (
                                        <th className={classNames("selection-column", getHeaderCSS({ columnName: null, columnIndex: -1 }))}>
                                            {multiSelect && (
                                                <bd.Toggle
                                                    size="sm"
                                                    disableRipple
                                                    indeterminate={selectedIndices.length > 0 && selectedIndices.length < data.length}
                                                    model={selectedIndices.length === data.length}
                                                    setModel={toggleAllSelection}
                                                />
                                            )}
                                        </th>
                                    )}
                                    {columns.map((c: ColumnProps, cIndex) => (
                                        <th key={cIndex} {...getTHProps(c, cIndex)}>
                                            {c.title ?? c.Header}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                        )}
                        <tbody>
                            {data.map((row: object, rowIndex: number) => (
                                <tr
                                    key={rowIndex}
                                    className={isSelected(rowIndex) ? "selected" : null}
                                    onClick={(event) => handleRowClick(row, rowIndex, event)}
                                    onDoubleClick={(event) => onRowDoubleClick && onRowDoubleClick({ row, rowIndex, event })}
                                >
                                    {canShowCheckBox && (
                                        <td
                                            onDragOver={(ev) => onDragOverHandler(ev, rowIndex, -1)}
                                            onDragEnter={(ev) => onDragOverHandler(ev, rowIndex, -1)}
                                            onDrop={(ev) => onDropHandler(ev, rowIndex, -2)}
                                            className={classNames(
                                                "cur-pointer",
                                                getCellCSS({ row, rowIndex, columnName: null, columnIndex: -1, value: null })
                                            )}
                                            onClick={() => handleCellClick(rowIndex)}
                                        >
                                            {draggable && (
                                                <span
                                                    className="cur-pointer"
                                                    draggable={draggable}
                                                    onDragStart={(ev) => onDragStartHandler(ev, rowIndex)}
                                                    onDragEnd={(ev) => onDragEndHandler(ev, rowIndex)}
                                                >
                                                    <SvgDragIndicator className="text-secondary-text" />
                                                </span>
                                            )}
                                            {multiSelect && (
                                                <bd.Toggle
                                                    size="sm"
                                                    disableRipple
                                                    model={isSelected(rowIndex)}
                                                    setModel={() => toggleSelection(rowIndex, false)}
                                                />
                                            )}
                                            {singleSelect && (
                                                <bd.Toggle
                                                    size="sm"
                                                    disableRipple
                                                    model={isSelected(rowIndex)}
                                                    setModel={() => toggleSelection(rowIndex, false)}
                                                    radio
                                                />
                                            )}
                                        </td>
                                    )}

                                    {columns.map((c: ColumnProps, columnIndex) => renderHandler(row, rowIndex, c, columnIndex))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                {(!data || data.length == 0) && children}
            </div>
            {actions && (
                <bd.Flex align="center" className="bg-shade-5 p-s-4 py-2 border-bottom" gap={3}>
                    <SvgSubdirectoryArrowRight className="rtl-d-none text-secondary-text" />
                    <SvgSubdirectoryArrowLeft className="ltr-d-none text-secondary-text" />
                    {actions}
                </bd.Flex>
            )}
        </>
    );
};
